import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'reducers/rootReducer'
import { useParams } from 'react-router-dom'
import { useAppDispatch } from 'hooks/useAppDispatch'
import { getRestaurantInfo } from 'actions/restaurantInfo.action'
import { getCategories } from 'actions/categories.action'
import { getSubCategories } from 'actions/subCategories.action'
import { getProducts } from 'actions/products.action'
import IRestaurantInfo from 'models/IRestaurantInfo'
import ICategory from 'models/ICategory'
import IProduct from 'models/IProduct'
import ISubCategory from 'models/ISubCategory'

export interface CategoryProps {}

export const useCategory = (props: CategoryProps) => {
  const { restaurantName, subCategoryId } = useParams<{
    restaurantName: string
    subCategoryId: string
  }>()

  const dispatch = useAppDispatch()

  const [subCategoryName, setSubCategoryName] = useState<string>('')
  const [currency, setCurrency] = useState<string>('')

  const restaurantInfo: IRestaurantInfo | null = useSelector(
    (state: RootState) => state.restaurantInfo.restaurantInfo
  )
  const categories: ICategory[] | null = useSelector(
    (state: RootState) => state.categories.categories
  )
  const subCategories: ISubCategory[] | null = useSelector(
    (state: RootState) => state.subCategories.subCategories
  )
  const products: IProduct[] | null = useSelector(
    (state: RootState) => state.products.products
  )

  useEffect(() => {
    if (subCategories && subCategoryId) {
      const name = subCategories.find(
        (subCategory) => subCategory.id === +subCategoryId
      )?.name

      setSubCategoryName(name || '')
    }
  }, [subCategories])

  useEffect(() => {
    try {
      if (!restaurantInfo) dispatch(getRestaurantInfo(restaurantName || ''))
      if (!categories || !categories.length)
        dispatch(getCategories(restaurantName || ''))
      if (!subCategories || !subCategories.length)
        dispatch(getSubCategories(restaurantName || ''))
      if (!products || !products.length)
        dispatch(getProducts(restaurantName || ''))
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if (restaurantInfo) {
      setCurrency(restaurantInfo.currency)

      const oldStyleAttributes = document.getElementsByClassName(
        'restaurant-variables'
      )

      if (!oldStyleAttributes.length) {
        const styleTag = document.createElement('style')

        const {
          headerBackground,
          headerTitle,
          headerText,
          headerIcon,
          bodyBackground,
          categoryBackground,
          categoryText,
          categoryActiveText,
          subCategoryTitle,
          productName,
          productPrice,
          productIngredientsBackground,
          productIngredientsText,
          priceListBackground,
          priceListItem,
          nutritionText,
          popularBackground,
          popularText,
          floatingActionsButtonBackground,
          floatingActionsButtonText,
          actionButtonsBackground,
          actionButtonsText,
          popupBackground,
          popupTitle,
          popupButtonBackground,
          popupButtonText,
          popupPlaceholder,
          cartFloatingIconBackground,
          cartFloatingIconText,
          addToCartBackground,
          addToCartText,
          cartScreenBackground,
          cartScreenText,
          cartItemBackground,
          cartButtonBackground,
          cartButtonText,
          cartQuantityBackground,
          cartQuantityText,
          checkoutInputBackground,
          checkoutPlaceholderColor,
          footer,
        } = restaurantInfo.colors

        styleTag.setAttribute('class', 'restaurant-variables')
        styleTag.innerHTML = `
          :root {
            --header-bg: ${headerBackground};
            --header-title: ${headerTitle};
            --header-text: ${headerText};
            --header-icon: ${headerIcon};
            --body-bg: ${bodyBackground};
            --category-bg: ${categoryBackground};
            --category-text: ${categoryText};
            --category-active-text: ${categoryActiveText};
            --sub-category-title: ${subCategoryTitle};
            --product-name: ${productName};
            --product-price: ${productPrice};
            --product-ingredients-bg: ${productIngredientsBackground};
            --product-ingredients-text: ${productIngredientsText};
            --price-list-bg: ${priceListBackground};
            --price-list-item: ${priceListItem};
            --nutrition-text: ${nutritionText};
            --popular-bg: ${popularBackground};
            --popular-text: ${popularText};
            --floating-actions-btn-bg: ${floatingActionsButtonBackground};
            --floating-actions-btn-text: ${floatingActionsButtonText};
            --action-btn-bg: ${actionButtonsBackground};
            --action-btn-text: ${actionButtonsText};
            --popup-bg: ${popupBackground};
            --popup-title: ${popupTitle};
            --popup-btn-bg: ${popupButtonBackground};
            --popup-btn-text: ${popupButtonText};
            --popup-placeholder: ${popupPlaceholder};
            --cart-floating-icon-bg: ${cartFloatingIconBackground};
            --cart-floating-icon-text: ${cartFloatingIconText};
            --add-to-cart-bg: ${addToCartBackground};
            --add-to-cart-text: ${addToCartText};
            --cart-screen-bg: ${cartScreenBackground};
            --cart-screen-text: ${cartScreenText};
            --cart-item-bg: ${cartItemBackground};
            --cart-btn-bg: ${cartButtonBackground};
            --cart-btn-text: ${cartButtonText};
            --cart-quantity-bg: ${cartQuantityBackground};
            --cart-quantity-text: ${cartQuantityText};
            --checkout-input-bg: ${checkoutInputBackground};
            --checkout-placeholder-color: ${checkoutPlaceholderColor};
            --footer-color: ${footer};
          }
        `
        document.head.appendChild(styleTag)

        const { isRTL } = restaurantInfo
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
        document
          .querySelector('.App')
          ?.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
      }
    }
  }, [restaurantInfo])

  return {
    ...props,
    restaurantName,
    subCategoryName,
    currency,
    products,
    isDeliveryOrdersPublished: restaurantInfo?.isDeliveryOrdersPublished,
  }
}
